import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowLeft } from 'lucide-react';
import { useCanonical } from './hooks/useCanonical';
import { usePageMeta } from './hooks/usePageMeta';

const EFFECTIVE_DATE = 'July 9, 2026';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold text-white mb-3">{title}</h2>
    <div className="space-y-3 text-zinc-400 leading-relaxed">{children}</div>
  </section>
);

function PrivacyPolicy() {
  useCanonical('https://hisab-kitab.maniac-tech.com/privacy-policy');
  usePageMeta(
    'Privacy Policy | Hisab Kitab',
    'How Hisab Kitab collects, uses and protects your data, including how SMS permission and your financial records are handled.',
  );
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <header className="border-b border-zinc-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="text-yellow-400">
              <BookOpen size={22} strokeWidth={2.5} />
            </div>
            <span className="font-bold text-zinc-200">Hisab Kitab</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-yellow-400 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-2">Privacy Policy</h1>
        <p className="text-sm text-zinc-500 mb-12">Last updated: {EFFECTIVE_DATE}</p>

        <Section title="Introduction">
          <p>
            Hisab Kitab (&quot;Hisab Kitab&quot;, &quot;we&quot;, &quot;us&quot;, or
            &quot;our&quot;) is a personal finance app developed by Abhishek Jain that helps you
            track income and expenses, including by detecting transactions from SMS messages sent
            by banks, wallets, and payment apps. This Privacy Policy explains what information we
            collect through the Hisab Kitab Android app, how we use and share it, and the choices
            you have.
          </p>
          <p>
            By creating an account or using the app, you agree to the collection and use of
            information as described here. If you do not agree, please do not use the app.
          </p>
        </Section>

        <Section title="Information We Collect">
          <p>
            <span className="text-zinc-200 font-semibold">Account information.</span> When you
            sign in with Google, we receive your name, email address, profile photo, and a unique
            account identifier from Google/Firebase Authentication. This identifier is used as
            your primary key in our database.
          </p>
          <p>
            <span className="text-zinc-200 font-semibold">Financial data you enter.</span>{' '}
            Transactions, accounts, categories, budgets, and notes you create or edit in the app.
          </p>
          <p>
            <span className="text-zinc-200 font-semibold">SMS messages (with your permission).</span>{' '}
            If you grant the SMS permission, Hisab Kitab reads SMS messages on your device — both
            new messages as they arrive and, if you run a scan, messages already in your inbox —
            in order to detect and pre-fill financial transactions (e.g. bank debit/credit
            alerts, UPI notifications, card statements). The sender and text of messages the app
            processes for this purpose are sent to and stored on our servers so that a
            transaction can be parsed, presented to you for review, and kept in sync across app
            sessions. You can review, edit, or discard any transaction detected this way before it
            is saved, and you can revoke the SMS permission at any time from your device
            settings — the app will simply stop detecting transactions automatically and you can
            continue to add them manually.
          </p>
          <p>
            <span className="text-zinc-200 font-semibold">Expense-splitting contacts.</span> If
            you use the expense-splitting feature, we store the name, and — if you provide them —
            the email address and/or phone number, of the people you add to a shared expense or
            group, along with the balances between you. This applies even if the other person does
            not use Hisab Kitab; if you add someone who is not an app user, please only do so with
            their knowledge.
          </p>
          <p>
            <span className="text-zinc-200 font-semibold">Usage and diagnostic data.</span> We
            collect basic engagement metrics (such as how often you open the app and which
            features you use), app version and device/OS information, and crash or error reports
            generated by the app, to help us fix bugs and improve the product.
          </p>
        </Section>

        <Section title="How We Use Your Information">
          <p>We use the information described above to:</p>
          <ul className="list-disc list-inside space-y-1.5">
            <li>Create and maintain your account and keep your data in sync across sessions</li>
            <li>Detect, categorize, and display your transactions</li>
            <li>Provide features such as budgets, accounts, and expense splitting</li>
            <li>Diagnose crashes and errors, and improve app performance and reliability</li>
            <li>Understand how the app is used so we can improve it</li>
            <li>Communicate with you about your account or provide support when you contact us</li>
          </ul>
          <p>We do not sell your personal information, and we do not use it for advertising.</p>
        </Section>

        <Section title="How We Share Your Information">
          <p>
            We do not share your personal information with third parties except with the service
            providers that help us run Hisab Kitab, each of which processes data on our behalf
            under its own terms:
          </p>
          <ul className="list-disc list-inside space-y-1.5">
            <li>
              <span className="text-zinc-200 font-semibold">Firebase (Google)</span> — for
              sign-in, analytics, and crash reporting
            </li>
            <li>
              <span className="text-zinc-200 font-semibold">Supabase</span> — our database
              provider, where your account and financial data are stored
            </li>
            <li>
              <span className="text-zinc-200 font-semibold">Sentry</span> — for error monitoring
            </li>
          </ul>
          <p>
            If you use expense splitting, limited information (such as your name and the shared
            expense amounts) is visible to the other members of that split. We may also disclose
            information if required by law or to protect the rights, safety, or property of Hisab
            Kitab, our users, or others.
          </p>
        </Section>

        <Section title="Data Storage & Security">
          <p>
            Your data is stored in our Supabase (PostgreSQL) database and access is scoped to
            your account using row-level security tied to your authenticated identity. All
            requests to our backend require a valid, verified sign-in token. While we take
            reasonable technical and organizational measures to protect your information, no
            method of transmission or storage is 100% secure, and we cannot guarantee absolute
            security.
          </p>
        </Section>

        <Section title="Data Retention">
          <p>
            We retain your account and financial data for as long as your account is active, so
            that your transaction history remains available to you. If you would like your data
            deleted, see &quot;Your Rights &amp; Choices&quot; below.
          </p>
        </Section>

        <Section title="Your Rights & Choices">
          <ul className="list-disc list-inside space-y-1.5">
            <li>You can edit or delete individual transactions, accounts, budgets, and splits directly in the app.</li>
            <li>You can revoke the SMS permission at any time from your device&apos;s app settings.</li>
            <li>
              You can request a copy of your data, or request that we delete your account and
              associated data, by contacting us at the email below.
            </li>
          </ul>
        </Section>

        <Section title="Children's Privacy">
          <p>
            Hisab Kitab is not directed to children, and we do not knowingly collect information
            from anyone under the age of 13. If you believe a child has provided us with personal
            information, please contact us so we can remove it.
          </p>
        </Section>

        <Section title="Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. If we make material changes,
            we will update the &quot;Last updated&quot; date above and, where appropriate, notify
            you in the app.
          </p>
        </Section>

        <Section title="Contact Us">
          <p>
            If you have questions about this Privacy Policy or how we handle your data, contact us
            at{' '}
            <a
              href="mailto:caffeinatedDuo@gmail.com"
              className="text-yellow-400 hover:underline"
            >
              caffeinatedDuo@gmail.com
            </a>
            .
          </p>
        </Section>
      </main>

      <footer className="border-t border-zinc-900 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-xs text-zinc-600">
          © {new Date().getFullYear()} Hisab Kitab. Made with ❤️ in India.
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
